import currency from "currency.js";
import { object, string } from "yup";

import { ProductFormData } from "@/components/screens/ProductFormPage/types";
import { MovementType, Type } from "@/components/templates/StockMovementModal/form-data";
import IProduct, { IProductInput, ImageData } from "@/interfaces/product.interface";
import { ISkuInput } from "@/interfaces/sku.interface";
import { ProductFormMessages } from "@/messages/products/product-form.messages";
import {
  normalizeVariationsToBackend,
  normalizeVariationsToFrontend,
} from "@/helpers/variation-helpers";

export const buildInitialData = (
  customerId: number,
  product: IProduct | null,
  isEditing: boolean,
  selectedCategories: number[] = []
): ProductFormData => {
  const normalizedVariations = product?.variations
    ? normalizeVariationsToFrontend(product.variations)
    : [];

  return {
    id: product?.id ?? 0,
    customerId: product?.customerId ?? customerId,
    active: product?.active ?? true,
    code: product?.code ?? "",
    title: product?.title ?? "",
    shortDescription: product?.shortDescription ?? "",
    description: product?.description ?? "",
    brand: product?.brand ?? "",
    ncm: product?.ncm ?? "",
    variationCode: product?.variationCode ?? "",
    variations: normalizedVariations,
    warrantyTime: product?.warrantyTime ?? null,
    pickUpInStore: product?.pickUpInStore ?? false,
    externalReference: product?.externalReference ?? "",
    skus: product?.skus ?? [],
    productImages: product?.productImages ?? [],
    skusToDelete: [],
    selectedCategories: selectedCategories,
    isEditing: isEditing,
    isEditingSku: false,
    sku: null,
    needComment: product?.needComment ?? false,
    groupVariations: product?.groupVariations ?? false,
    useShortDescriptionInSales: product?.useShortDescriptionInSales ?? false,
    videoUrl: product?.videoUrl || product?.skus?.find((s) => s.videoUrl)?.videoUrl || "",
  };
};

export const buildFormSchema = (messages: ProductFormMessages) =>
  object().shape({
    code: string().required(messages.errorRequiredProductCode),
    title: string().required(messages.errorRequiredProductTitle),
    // Descrições não bloqueiam o salvamento: o texto excedente é truncado no envio
  });

export const buildDefaultSKU = (formData: ProductFormData): ISkuInput => {
  const skuCode = `${formData.code}-${Date.now()}`;

  const defaultSKU: ISkuInput = {
    id: 0,
    code: skuCode,
    title: `${formData.title} - SKU`,
    imageUrl: "",
    videoUrl: "",
    imagePhisicalAddress: "",
    mainPrice: 0,
    promoPrice: 0,
    depth: 0,
    active: true,
    productCode: formData.code,
    stock: 0,
    stockMovement: {
      skuId: 0,
      movementType: MovementType.PRODUCT,
      movementDate: new Date().toISOString(),
      type: Type.ENTRY,
      quantity: 0,
      comments: "",
      documentNumber: "",
    },

    kitsToDelete: [],
    kitComponents: [],
  };

  return defaultSKU;
};

export const normalizeProductCreationData = (formData: ProductFormData): IProductInput => {
  const normalizedVariations = formData.variations
    ? normalizeVariationsToBackend(formData.variations)
    : [];

  const normalizedFormData: IProductInput = {
    ...formData,
    skus: [],
    description: (formData.description || "").slice(0, 4096),
    shortDescription: (formData.shortDescription || "").slice(0, 500),
    externalReference: formData.title,
    variations: normalizedVariations,
    // Converter string vazia para null no warrantyTime
    warrantyTime: formData.warrantyTime === "" ? null : formData.warrantyTime,
  };

  return normalizedFormData;
};

export const normalizeSkuCreationFormData = (
  formData: ISkuInput,
  product: IProduct,
  customerId: number,
  isEditing: boolean
): ISkuInput => {
  const { code, id } = product;
  const {
    width,
    height,
    weight,
    depth,
    startPromoPrice,
    endPromoPrice,
    variations,
    kitComponents,
  } = formData;

  const normalizedFormData: ISkuInput = {
    ...formData,
    customerId: customerId,
    productCode: code,
    productId: id,
    width: width ? Number(width) : 0,
    height: height ? Number(height) : 0,
    weight: weight ? Number(weight) : 0,
    depth: depth ? Number(depth) : 0,
    startPromoPrice: startPromoPrice ? startPromoPrice : null,
    endPromoPrice: endPromoPrice ? endPromoPrice : null,
    imagePhisicalAddress: formData.imagePhisicalAddress || "",
    variations,
    kitComponents: isEditing ? [] : kitComponents,
  };

  return normalizedFormData;
};

export const normalizeSkuEditingFormData = (formData: ISkuInput): ISkuInput => {
  const mainPrice = Number(currency(formData.mainPrice));
  const promoPrice = Number(currency(formData.promoPrice));
  const { width, height, weight, depth, startPromoPrice, endPromoPrice, variations } = formData;

  const normalizedFormData: ISkuInput = {
    ...formData,
    imageUrl: "",
    mainPrice,
    promoPrice,
    width: width ? Number(width) : 0,
    height: height ? Number(height) : 0,
    weight: weight ? Number(weight) : 0,
    depth: depth ? Number(depth) : 0,
    startPromoPrice: startPromoPrice ? startPromoPrice : null,
    endPromoPrice: endPromoPrice ? endPromoPrice : null,
    imagePhisicalAddress: formData.imagePhisicalAddress || "",
    variations,
  };

  return normalizedFormData;
};

export const buildImageFormData = (imageData: ImageData, productId: string) => {
  const formData = new FormData();

  const fileNameWithExtension = imageData.file.name;
  const rawBaseName = fileNameWithExtension.replace(/\.[^/.]+$/, "");
  const rawExtension = (fileNameWithExtension.split(".").pop() || "").toLowerCase();

  // Sanitize base name: remove accents, replace spaces and invalid chars with '_'
  const alphanumericBase = rawBaseName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "")
    .slice(0, 80);

  const fallbackBase = `image${Date.now()}`;
  const sanitizedBaseName = (alphanumericBase || fallbackBase).toLowerCase();
  const sanitizedFullName = `${sanitizedBaseName}.${rawExtension || "png"}`;
  const isMain = imageData.main ? "true" : "false";

  formData.append("productId", productId);
  // API expects capitalized "File" field name and safe filename
  formData.append("File", imageData.file, sanitizedFullName);
  formData.append("extension", rawExtension || "png");
  formData.append("name", sanitizedBaseName || "image");
  formData.append("logicalAddress", "");
  formData.append("phisicalAddress", "");
  formData.append("main", isMain);

  return formData;
};
